import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  startWith,
  tap,
} from 'rxjs';
import { DatePicker } from 'primeng/datepicker';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { RadioButton } from 'primeng/radiobutton';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { ReportsApi } from '../../../../../shared/services/api/reports.api';
import { ToastSeverity } from '../../../../../shared/enums/toast-severity.enum';
import { ComparisionCycle } from '../../../../../shared/enums/comparision-cycle.enum';
import { ReportName } from '../../../../../shared/enums/report-name.enum';
import { ReportGenerateFormModel } from './form-group/report-generate-form.model';
import {
  BASIC_REPORT_STATUS_OPTIONS,
  COMPARATIVE_REPORT_STATUS_OPTIONS,
  REPORT_CYCLE_OPTIONS,
  REPORT_DRAFT_STATUS,
  REPORT_HALF_OPTIONS,
  REPORT_MONTH_OPTIONS,
  REPORT_NAME_OPTIONS,
  REPORT_QUARTER_OPTIONS,
  ReportGenerateRequest,
} from '../../../../../shared/models/raport-generowanie';
import { Button } from 'primeng/button';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';
import { ReportGenerateFormBuilder } from './form-group/report-generate-form.builder';
import { HttpResponseService } from '../../../../../core/services/http-error-response.service';
import { IsReportTypePipe } from '../../pipes/is-report-type.pipe';
import { IsNotNullPipe } from '../../pipes/is-not-null.pipe';
import { SelectOption } from '../../../../../shared/models/select-option';
import { DictionaryItemDto } from '../../../../../shared/models/raport';

@Component({
  selector: 'app-admin-panel-reports-generate',
  imports: [
    ReactiveFormsModule,
    DatePicker,
    MultiSelect,
    Select,
    ControlErrorDirective,
    Button,
    RadioButton,
    RequiredLabelDirective,
    IsReportTypePipe,
    IsNotNullPipe,
  ],
  templateUrl: './admin-panel-reports-generate.component.html',
  styleUrl: './admin-panel-reports-generate.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReportsApi],
  standalone: true,
})
export class AdminPanelReportsGenerateComponent implements OnInit {
  protected readonly isSubmitting = signal(false);
  protected readonly applicationTypes = input<DictionaryItemDto[]>();
  protected applicationTypeOptions: SelectOption<string>[] = [];
  protected readonly CompareCycle = ComparisionCycle;

  protected readonly nameOptions = REPORT_NAME_OPTIONS;
  protected readonly cycleOptions = REPORT_CYCLE_OPTIONS;

  protected readonly halfOptions = REPORT_HALF_OPTIONS;
  protected readonly quarterOptions = REPORT_QUARTER_OPTIONS;
  protected readonly monthOptions = REPORT_MONTH_OPTIONS;
  protected readonly reportNames = ReportName;

  protected formGroup = signal<FormGroup<ReportGenerateFormModel>>(
    ReportGenerateFormBuilder.createFormGroup(),
  );

  readonly #destroyRef = inject(DestroyRef);
  readonly #reportsApi = inject(ReportsApi);
  readonly #router = inject(Router);
  readonly #messageService = inject(MessageService);
  #httpResponseService = inject(HttpResponseService);

  constructor() {
    this.#applyConditionalValidators();
  }

  ngOnInit(): void {
    this.applicationTypeOptions =
      this.applicationTypes()
        ?.filter((item) => item.key !== 'ALL')
        .map((item) => ({
          label: item.label,
          value: item.key,
        })) ?? [];
  }

  protected get cykl(): ComparisionCycle | null | undefined {
    return this.formGroup().controls.cykl.value;
  }

  protected get statusOptions(): SelectOption<string>[] {
    if (
      this.formGroup().controls.nazwa.value !==
      ReportName.BASIC_APPLICATION_REPORT
    ) {
      return COMPARATIVE_REPORT_STATUS_OPTIONS;
    }

    return BASIC_REPORT_STATUS_OPTIONS;
  }

  protected onCancel(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.reports}`,
    ]);
  }

  protected onGenerate(): void {
    this.formGroup().markAllAsTouched();
    this.formGroup().updateValueAndValidity();

    if (this.formGroup().invalid) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Brak wymaganych danych',
        detail: 'Uzupelnij wymagane pola w formularzu.',
        life: 4000,
      });
      return;
    }

    const payload = this.#buildRequest();

    this.isSubmitting.set(true);
    this.#reportsApi
      .generateReport$(payload)
      .pipe(
        tap((response) => {
          this.#handleSuccess();
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            err.error.message ?? 'Wystąpił błąd podczas generowania raportu.',
          );
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  #handleSuccess(): void {
    this.#messageService.add({
      severity: ToastSeverity.INFO,
      summary: 'Raport został zlecony',
      detail:
        'Generowanie raportu może potrwać dłuższą chwilę. Po zakończeniu status raportu zmieni się na „Wygenerowany”.',
      life: 8000,
    });

    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.reports}`,
    ]);
  }

  #buildRequest(): ReportGenerateRequest {
    const value = this.formGroup().getRawValue();
    const selectedApplicationTypes = value.rodzajWnioskuCode ?? [];
    const allApplicationTypesSelected = this.#isAllSelected(
      selectedApplicationTypes,
      this.applicationTypeOptions,
    );

    const selectedStatuses = value.statusWniosku ?? [];
    const allStatusesSelected =
      value.nazwa !== ReportName.BASIC_APPLICATION_REPORT &&
      this.#isAllSelected(selectedStatuses, COMPARATIVE_REPORT_STATUS_OPTIONS);

    const payload: ReportGenerateRequest = {
      nazwa: value.nazwa!,
      rodzajWnioskuCode: allApplicationTypesSelected
        ? ['ALL']
        : selectedApplicationTypes.length
          ? selectedApplicationTypes
          : null,
      statusWniosku: allStatusesSelected
        ? ['ALL']
        : selectedStatuses.length
          ? selectedStatuses
          : null,
    };
    if (value.nazwa === ReportName.BASIC_APPLICATION_REPORT) {
      payload.dataOd = this.#formatDate(value.dataOd);
      payload.dataDo = this.#formatDate(value.dataDo);
      payload.cykl = value.cykl ?? undefined;
    }

    if (value.nazwa === ReportName.COMPARATIVE_APPLICATION_REPORT) {
      payload.cykl = value.cykl ?? undefined;
      payload.rok = value.rok?.getFullYear() ?? undefined;
      payload.doRoku = value.doRoku?.getFullYear() ?? undefined;

      if (value.cykl === ComparisionCycle.HALF_YEAR) {
        payload.polrocze = value.polrocze ?? undefined;
        payload.doPolrocza = value.doPolrocza ?? undefined;
      }

      if (value.cykl === ComparisionCycle.QUARTER) {
        payload.kwartal = value.kwartal ?? undefined;
        payload.doKwartalu = value.doKwartalu ?? undefined;
      }

      if (value.cykl === ComparisionCycle.MONTH) {
        payload.miesiac = value.miesiac ?? undefined;
        payload.doMiesiac = value.doMiesiac ?? undefined;
      }
    }

    return payload;
  }

  #formatDate(date: Date | null): string | undefined {
    if (!date) {
      return undefined;
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  #isAllSelected(
    selectedValues: string[],
    options: SelectOption<string>[],
  ): boolean {
    return (
      options.length > 0 &&
      selectedValues.length === options.length &&
      options.every((option) => selectedValues.includes(option.value))
    );
  }

  protected getSelectedItemsLabel(values: string[] | null): string {
    const count = values?.length ?? 0;

    if (count === 0) {
      return 'Wybierz';
    }

    if (count === 1) {
      return 'Wybrano 1 element';
    }

    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (
      lastDigit >= 2 &&
      lastDigit <= 4 &&
      !(lastTwoDigits >= 12 && lastTwoDigits <= 14)
    ) {
      return `Wybrano ${count} elementy`;
    }

    return `Wybrano ${count} elementów`;
  }

  #applyConditionalValidators(): void {
    this.formGroup()
      .controls.nazwa.valueChanges.pipe(
        startWith(this.formGroup().controls.nazwa.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        ReportGenerateFormBuilder.applyConditionalRules(this.formGroup());
        this.#removeDraftStatusForBasicReport();
      });

    this.formGroup()
      .controls.cykl.valueChanges.pipe(
        startWith(this.formGroup().controls.cykl.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() =>
        ReportGenerateFormBuilder.applyConditionalRules(this.formGroup()),
      );
  }

  #removeDraftStatusForBasicReport(): void {
    const formGroup = this.formGroup();
    if (formGroup.controls.nazwa.value !== ReportName.BASIC_APPLICATION_REPORT) {
      return;
    }

    const selectedStatuses = formGroup.controls.statusWniosku.value;
    if (!selectedStatuses?.includes(REPORT_DRAFT_STATUS)) {
      return;
    }

    formGroup.controls.statusWniosku.setValue(
      selectedStatuses.filter(
        (status) => status !== REPORT_DRAFT_STATUS,
      ),
      { emitEvent: false },
    );
  }
}
